
import { ChatMessage } from "../../interfaces/chatbotInterface";
import axiosInstance from "../../service/axiosInstance";
import { API_DOMAIN, GET, NON_EXISTING, POST } from "../../utility/constants";
import { timeout } from "../../utility/helper";

// const mockBorrowerName = "Hills & Meyer";

// ---------------------------
// All mock Q&A lives here
// ---------------------------
export const mockResponses: Record<string, string> = {
  // [`Available borrowing base for ${mockBorrowerName}?`]:
  //   `The current available borrowing base for ${mockBorrowerName} is <strong>$12.5M</strong> as of today. This reflects eligible receivables net of ineligibles and reserves.`,

  // "Top 10 delinquent accounts":
  //   "Here are the top 10 delinquent accounts (by outstanding balance):\n1. Acme Corp – $1.2M\n2. Beta Inc – $900k\n3. Delta LLC – $850k\n4. Horizon Ltd – $730k\n5. Orion Co – $690k\n6. Nova Partners – $650k\n7. Apex Systems – $600k\n8. Zenith Group – $540k\n9. Vertex Ltd – $500k\n10. Summit Inc – $480k.",

  // [`What is the current available borrowing base for ${mockBorrowerName}?`]:
  //   `As of the latest calculation, ${mockBorrowerName} has an available borrowing base of <strong>$12.5M</strong>.`,

  // "Show ineligible collateral with reasons":
  //   "Here are the ineligible collateral items:\n- Customer A – $200k (aged > 90 days)\n- Customer B – $150k (cross-aged)\n- Customer C – $100k (contra account)\n- Customer D – $75k (foreign receivable, not covered)\n- Customer E – $60k (concentration limit exceeded).",

  // "Which 10 accounts have the largest delinquent balances?":
  //   "The 10 accounts with the largest delinquent balances are the same as the 'Top 10 delinquent accounts' list, led by Acme Corp and Beta Inc. Full details are available in the AR Ineligible Report.",

  // [`What percentage of ${mockBorrowerName}'s overall AR is tied to its largest customer?`]:
  //   `${mockBorrowerName}'s largest customer is <strong>Acme Corp</strong>, which represents <strong>18%</strong> of total AR ($2.25M out of $12.5M).`,

  // "What is cross-age and how does it differ from aged credits and contras?":
  //   "Cross-age refers to a rule where if more than a set percentage (e.g., 50%) of a customer's receivables are aged beyond eligibility (e.g., 90 days), then the entire balance is deemed ineligible. Aged credits are past-due amounts, while contras are receivables offset by payables to the same customer/vendor.",

  // "How are delinquent AR calculated in LoanWatch?":
  //   "Delinquent AR is calculated as any receivable that has passed its due date. LoanWatch categorizes them by 0–30, 31–60, 61–90, and >90 days past due, and flags balances accordingly.",

  // "How do I upload new AR data?":
  //   "To upload new AR data:\n" +
  //   "1. Go to the <strong>File Import</strong> page and select an independent/child client.\n" +
  //   "2. Click <strong>Upload New File</strong>.\n" +
  //   "3. In the <strong>Upload Files</strong> page, choose:\n   • <strong>AR Aging</strong> in the Document Type field\n   • A collateral in the AR Collateral field\n   • An As of Date\n" +
  //   "4. Upload the file and select the sheet.\n" +
  //   "5. Click <strong>Save</strong>.\n" +
  //   "6. Click the <strong>Data Map</strong> icon.\n" +
  //   "7. Exclude rows if needed, then click <strong>Next</strong>.\n" +
  //   "8. Map all required fields, then click <strong>Save Changes</strong>.\n" +
  //   "9. Click <strong>Next</strong>, then <strong>Proceed to Import</strong>.\n" +
  //   "10. Wait for the upload to finish importing.",

  // "Where do I go to create a new report?":
  //   "You can create a new report by going to the <strong>Reports</strong> tab in LoanWatch. Choose the report type (Borrowing Base, AR Ineligible, etc.), apply filters, and generate."
};

// ---------------------------
// Chat history generator
// ---------------------------
const generateMockChatHistory = (): ChatMessage[] => {
  const mock: ChatMessage[] = [];
  // for (let i = 1; i <= 120; i++) {
  //   mock.push({
  //     sender: i % 2 === 0 ? "human" : "ai",
  //     text:
  //       i % 2 === 0
  //         ? `User message number ${i}`
  //         : `Bot reply number ${i}`,
  //   });
  // }
  return mock;
};

export const getMockChatHistory = async (
  sessionUuid: string,
  page: number,
  pageSize: number = 10
): Promise<ChatMessage[]> => {
  const mock = generateMockChatHistory();
  await timeout(500);

  const start = (page - 1) * pageSize;
  const end = page * pageSize;
  let pageData = mock.slice(start, end);

  if (page === 1) {
    // const queries = Object.keys(mockResponses).slice(0, 5); // first 5 queries
    pageData = [
      ...pageData,
      {
        recordId: NON_EXISTING,
        sessionUuid: sessionUuid,
        senderType: "ai",
        content: "Hey there 👋 I’m LoanWatch Pulse. \nHow can I help?",
        isReserved: false,
        type: "options",
        // suggestedQueries: [...queries, "See more sample queries"],
      },
    ];
  }

  return pageData;
};

// ---------------------------
// Suggested queries
// ---------------------------
type SuggestedResponse = {
  queries: string[];
  totalSize: number;
  hasMore: boolean;
};

let batchIndex = 0;
const allQueries = Object.keys(mockResponses);

export const getMoreSuggestedQueries = async (): Promise<SuggestedResponse> => {
  await timeout(500);

  const initialSize = 5; // already shown in greeting
  const batchSize = 5;
  const totalSize = allQueries.length;
  const totalBatches = Math.ceil((totalSize - initialSize) / batchSize);

  const start = initialSize + batchIndex * batchSize;
  const end = start + batchSize;
  const queries = allQueries.slice(start, end);

  if (queries.length === 0) {
    return { queries: [], totalSize, hasMore: false };
  }

  batchIndex++;
  return {
    queries,
    totalSize,
    hasMore: batchIndex < totalBatches,
  };
};

export const getReservedChatMessagesByCreatedBy = async (createdBy: string) => {
  const response = await axiosInstance.request({
    url: `${API_DOMAIN}/chat/messages/search/findAllReservedByCreatedBy`,
    method: GET,
    params: { createdBy: createdBy, pageNo: 0, pageSize: 99999 },
  });
  return response.data.content as ChatMessage[];
};

export const postChatMessage = async (values: ChatMessage) => {
  const response = await axiosInstance.request({
    url: `${API_DOMAIN}/chat/messages/`,
    method: POST,
    data: {
      recordId: values.recordId,
      sessionUuid: values.sessionUuid,
      senderType: values.senderType,
      content: values.content,
      isReserved: values.isReserved,
    },
  });
  return response.data as ChatMessage;
};